import { isRunningAsStandalonePwa } from './pwa'

export const OPEN_PDF_EVENT = 'alex-open-pdf'

export type OpenPdfDetail = {
  url: string
  title?: string
}

const nativeOpen =
  typeof window !== 'undefined' ? window.open.bind(window) : null

export function isPdfUrl(url: string): boolean {
  if (!url) return false
  if (url.startsWith('blob:')) return true
  try {
    const { pathname, search } = new URL(url, window.location.origin)
    return `${pathname}${search}`.toLowerCase().includes('pdf')
  } catch {
    return url.toLowerCase().includes('pdf')
  }
}

/** В PWA — внутренний просмотрщик; в браузере — новая вкладка */
export function openPdf(url: string, title = 'PDF') {
  if (!url) return

  if (isRunningAsStandalonePwa()) {
    window.dispatchEvent(
      new CustomEvent<OpenPdfDetail>(OPEN_PDF_EVENT, {
        detail: { url, title },
      })
    )
    return
  }

  nativeOpen?.(url, '_blank', 'noopener,noreferrer')
}

/** Перехват ссылок и window.open(PDF) только в установленном PWA */
export function installPwaPdfGuards() {
  if (typeof window === 'undefined') return
  const flag = window as Window & { __alexPdfGuards?: boolean }
  if (flag.__alexPdfGuards) return
  flag.__alexPdfGuards = true

  if (nativeOpen) {
    window.open = (url, target, features) => {
      if (
        typeof url === 'string' &&
        isRunningAsStandalonePwa() &&
        isPdfUrl(url)
      ) {
        openPdf(url)
        return null
      }
      return nativeOpen(url, target, features)
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!isRunningAsStandalonePwa()) return
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
      }

      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a')
      if (
        !anchor ||
        anchor.hasAttribute('download') ||
        anchor.hasAttribute('data-alex-pdf-download')
      ) {
        return
      }

      const href = anchor.href
      // blob: только для программного openPdf, не перехватываем как ссылку
      if (!href || href.startsWith('blob:') || !isPdfUrl(href)) return

      event.preventDefault()
      openPdf(href, anchor.textContent?.trim() || 'PDF')
    },
    true
  )
}
